/**
 * Scroll reveal.
 *
 * IntersectionObserver rather than a scroll listener: the browser does the
 * intersection maths off the main thread, so this costs nothing per frame.
 * Elements are unobserved once shown — reveals happen once, not on every pass.
 */

import { $$, prefersReducedMotion } from '@/lib/dom'

export function initReveal(): void {
  const targets = $$('[data-reveal]')
  if (targets.length === 0) return

  // With reduced motion the CSS already renders these visible; skip the work.
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
    targets.forEach((node) => node.classList.add('is-visible'))

    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return

        const node = entry.target as HTMLElement

        // Stagger siblings so a grid cascades rather than snapping in at once.
        const index = Number(node.dataset.revealIndex ?? '0')
        node.style.setProperty('--reveal-delay', `${Math.min(index, 8) * 55}ms`)
        node.classList.add('is-visible')

        observer.unobserve(node)
      })
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 },
  )

  targets.forEach((node) => observer.observe(node))
}
